import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tag, Typography, Table, Input, Space, Dropdown, Button, App, Tooltip, Image, Avatar, Segmented } from 'antd';
import { MoreOutlined, EyeOutlined, EditOutlined, DeleteOutlined, PlusOutlined, PictureOutlined, FileExcelOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { listExercises, deleteExercise } from '../../services/exercises';
import { exportToExcel } from '../../utils/excel';

const { Title, Text } = Typography;
const { useApp } = App;

const ExercisesList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [filterType, setFilterType] = useState('ALL'); // ALL, WITH_VIDEO, PENDING

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (page, pageSize, srt, q, tabFilter) => {
    setLoading(true);
    try {
      const { items, total } = await listExercises({
        search: q || undefined,
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        sortField: srt?.field,
        sortOrder: srt?.order,
        filterType: tabFilter || filterType, 
      });

      let filteredItems = items || [];

      // Filtrado client-side como respaldo en caso de que el backend no procese filterType
      const activeFilter = tabFilter || filterType;
      if (activeFilter === 'WITH_VIDEO') {
        filteredItems = filteredItems.filter(item => item.variants_info?.enabled > 0);
      } else if (activeFilter === 'PENDING') {
        filteredItems = filteredItems.filter(item => item.status === 'Incompleto');
      }

      setData(filteredItems);
      setPagination(prev => ({
        ...prev,
        current: page || prev.current,
        pageSize: pageSize || prev.pageSize,
        total: total || 0
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista de ejercicios.');
    } finally {
      setLoading(false);
    }
  }, [pagination, filterType, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search, filterType);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    fetchData(1, pagination.pageSize, sorter, value || '', filterType);
  };

  const onFilterChange = (value) => {
    setFilterType(value);
    fetchData(1, pagination.pageSize, sorter, search, value);
  };

  // Función para exportar todos los ejercicios a Excel
  const handleExportToExcel = async () => {
    setExporting(true);
    try {
      const { items } = await listExercises({
        search: search || undefined,
        page: 1,
        pageSize: 500, 
        sortField: sorter?.field,
        sortOrder: sorter?.order,
        filterType,
      });

      if (!items || items.length === 0) {
        message.warning('No hay ejercicios para exportar');
        return;
      }

      const excelData = items.map((exercise, index) => {
        const primaryMuscles = (exercise.primary_muscles || []).map(m => m.name).join(', ');
        const secondaryMuscles = (exercise.secondary_muscles || []).map(m => m.name).join(', ');
        const styles = (exercise.training_styles || []).map(s => s.name).join(', ');
        const types = (exercise.execution_types || []).map(s => s.name).join(', ');

        let musclesText = primaryMuscles;
        if (secondaryMuscles) musclesText += ` (Secundarios: ${secondaryMuscles})`;

        return {
          '#': index + 1,
          'Ejercicio': exercise.title || '-',
          'Músculos': musclesText || '-',
          'Unilateral / Bilateral': exercise.is_unilateral ? 'Unilateral' : 'Bilateral',
          'Estilo': styles || '-',
          'Tipo de Ejecución': types || '-',
          'Variantes': exercise.variants_info?.formatted || '-',
          'Estado': exercise.status || '-',
        };
      });

      const columnWidths = [
        { wch: 5 },
        { wch: 30 },
        { wch: 35 },
        { wch: 20 },
        { wch: 25 },
        { wch: 20 },
        { wch: 20 },
        { wch: 15 },
      ];

      exportToExcel(excelData, columnWidths, 'Ejercicios', 'ejercicios');

    } catch (err) {
      console.error('Error al exportar a Excel:', err);
      message.error('No se pudo exportar los ejercicios a Excel');
    } finally {
      setExporting(false);
    }
  };

  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    switch (action) {
      case 'view':
        navigate(`/exercises/view/${record.id}`);
        break;
      case 'edit':
        navigate(`/exercises/${record.id}/edit`);
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar ejercicio?',
          content: `${record.title || 'Ejercicio'}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deleteExercise(record.id);
              message.success('Ejercicio eliminado');

              const nextPage = (data.length === 1 && pagination.current > 1)
                ? pagination.current - 1
                : pagination.current;
              setPagination(prev => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search, filterType);
            } catch {
              message.error('No se pudo eliminar el ejercicio');
            }
          },
        });
        break;
      default:
        break;
    }
  };


  const getActionsMenu = (record) => {
    const menuItems = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: 'Ver ejercicio',
      },
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar ejercicio',
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Eliminar ejercicio',
        danger: true,
      },
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    {
      title: 'EJERCICIO',
      key: 'exercise',
      render: (_, r) => {
        const primary = (r.primary_muscles || []).map(m => m.name).join(' · ');
        const secondary = (r.secondary_muscles || []).map(m => m.name).join(', ');

        return (
          <div>
              <Text bold style={{ fontSize: '15px', display: 'block' }}>
                {r.title || '-'} {r.is_unilateral && <Text type="secondary" style={{ fontWeight: 'normal', fontSize: '13px' }}>(unilateral)</Text>}
              </Text>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                {primary || 'Sin músculos primarios'}
                {secondary ? ` · ${secondary}` : ''}
              </Text>
            </div>
        );
      },
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
    },
    {
      title: 'ESTILO',
      dataIndex: 'training_styles',
      key: 'training_styles',
      render: (styles) => {
        if (!styles || styles.length === 0) return <Text type="secondary">-</Text>;
        return (
          <Space size={[0, 4]} wrap>
            {styles.map(s => (
              <Tag key={s.id} color="purple" style={{ borderRadius: 12, padding: '0 10px' }}>
                {s.name}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'TIPO',
      dataIndex: 'execution_types',
      key: 'execution_types',
      render: (execTypes) => {
        if (!execTypes || execTypes.length === 0) return <Text type="secondary">-</Text>;
        return (
          <Space size={[0, 4]} wrap>
            {execTypes.map(s => (
              <Tag key={s.id} color="geekblue" style={{ borderRadius: 12, padding: '0 10px' }}>
                {s.name}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'VARIANTES',
      dataIndex: 'variants_info',
      key: 'variants_info',
      render: (info) => info?.formatted || '0 de 0 habilitadas',
    },
    {
      title: 'ESTADO',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const isComplete = status === 'Completo';
        return (
          <Tag 
            icon={isComplete ? <CheckCircleOutlined /> : <ClockCircleOutlined />} 
            color={isComplete ? 'success' : 'warning'}
            style={{ borderRadius: 12, padding: '2px 10px' }}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'ACCIONES',
      key: 'actions',
      align: 'center',
      width: 80,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={<Title level={3} style={{ marginBottom: 0 }}>Catálogo de Ejercicios</Title>}
        extra={
          <Space>
            <Tooltip title="Exportar a Excel">
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExportToExcel}
                loading={exporting}
                disabled={exporting}
              >
                Exportar Excel
              </Button>
            </Tooltip>
            <Tooltip title="Agregar ejercicio">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/exercises/add')}
              >
                Nuevo ejercicio
              </Button>
            </Tooltip>
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
            <Input.Search
              placeholder="Buscar ejercicio por nombre..."
              allowClear
              onSearch={onSearch}
              style={{ width: 360 }}
            />
            
            <Segmented
              options={[
                { label: 'Todos', value: 'ALL' },
                { label: 'Con video', value: 'WITH_VIDEO' },
                { label: 'Pendientes', value: 'PENDING' },
              ]}
              value={filterType}
              onChange={onFilterChange}
            />
          </Space>

          <Table
            rowKey={(r) => r.id}
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default ExercisesList;